import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ConfigService } from '../commons';
import {
  Cups,
  CodigoPostal,
  RazonSocial,
  TipoVia,
  InstallationSavedData,
  SavedData,
  PaymentSavedData,
  IContactCRMSearchData,
  IDraftData,
  CheckIbanSRRequest,
  CheckIbanSRResponse
} from '../interfaces';
import merge from 'ramda/src/merge';
import { Campaign, GenericResponse } from '@interfaces';
import { AttachmentsDraft, CheckContractAsyncResponse, CreateContractAsyncResponse, Tensions } from '../modules/new-contract-pymes/new-contract-pymes.model';


@Injectable({
  providedIn: 'root'
})
export class NewContractPymesService {
  indexContract = new BehaviorSubject<number>(0);
  validateCups = new BehaviorSubject<any>(null);
  validateCif = new BehaviorSubject<any>(null);
  savedData = new BehaviorSubject<SavedData>(null);
  viaTypes = new BehaviorSubject<any>(null);
  tipoVias = new BehaviorSubject<any>(null);
  codigoPostal = new BehaviorSubject<any>(null);
  poblaciones = new BehaviorSubject<any>(null);
  razonSocial = new BehaviorSubject<GenericResponse<string>>(null);
  installationCode = new BehaviorSubject<GenericResponse<string>>(null);
  contractCode = new BehaviorSubject<GenericResponse<{
    creadoAnteriormente: boolean,
    codigoCRMContrato: string
  }>>(null);
  codContrato = new BehaviorSubject<any>(null);
  contactoCRM = new BehaviorSubject<any>(null);

  constructor(private http: HttpClient, private config: ConfigService) { }

  getIndexContract(): Observable<number> {
    return this.indexContract.asObservable();
  }

  setIndexContract(index): void {
    this.indexContract.next(index);
  }

  getCupsValidation(cups: string) {
    return this.http.put(this.config.getUrl('pymes.newContract.validateCups', { cups }), {}).pipe(
      tap((res: GenericResponse<Cups>) => {
        this.validateCups.next(res);
      })
    );
  }

  public getCampaignsFiltered(
    ContactoCRM: string,
    TarifaSips: string,
    ZonaGeograficaCUPS: string,
    Consumo: string,
    FechaVigenciaFutura?: string
  ): Observable<GenericResponse<Campaign[]>> {
    const params = {
      TarifaSips,
      ZonaGeograficaCUPS,
      ContactoCRM,
      Consumo,
      FechaVigenciaFutura
    };
    if (!params.FechaVigenciaFutura) {
      delete params.FechaVigenciaFutura;
    }
    return this.http.get<GenericResponse<Campaign[]>>(this.config.getUrl('maestros.getCampaigns'), { params });
  }

  public getCampaignsFilteredPT(
    ContactoCRM: string,
    IdTension: string,
    IdCiclo: string,
    Consumo: string,
    IdPeriodo: string,
    FechaVigenciaFutura?: string
  ): Observable<GenericResponse<Campaign[]>> {
    const params = {
      ContactoCRM,
      IdTension,
      IdCiclo,
      Consumo,
      IdPeriodo,
      FechaVigenciaFutura
    };
    if (!params.FechaVigenciaFutura) {
      delete params.FechaVigenciaFutura;
    }
    return this.http.get<GenericResponse<Campaign[]>>(this.config.getUrl('maestros.getCampaignsPT'), { params });
  }

  getCifValidation(params: any) {
    return this.http.put(this.config.getUrl('pymes.newContract.validateCif'), {}, { params }).pipe(
      tap((res: GenericResponse<RazonSocial>) => {
        this.validateCif.next(res);
        this.setSavedData({ idBorrador: res.data?.idBorrador || null });
        if (res.info) {
          const dataToSave = merge(this.getSavedData().value || {}, { business: res.data });
          this.savedData.next(dataToSave);
        }
      })
    );
  }

  getSavedData() {
    return this.savedData;
  }

  setSavedData(data: any) {
    const dataToSave = merge(this.getSavedData().value || {}, data);
    this.savedData.next(dataToSave);
  }

  resetSavedData() {
    this.savedData.next(null);
  }

  getTipoVia(): Observable<GenericResponse> {
    return this.http.get(this.config.getUrl('maestros.getTipoVia')).pipe(
      tap((res: GenericResponse<TipoVia[]>) => {
        if (!this.tipoVias.value) {
          this.tipoVias.next(res);
        }
      })
    );
  }

  getCodigoPostal(codPostal: string): Observable<GenericResponse> {
    return this.http.get(this.config.getUrl('maestros.getCodigoPostal', { codPostal })).pipe(
      tap((res: GenericResponse<CodigoPostal>) => {
        this.codigoPostal.next(res);
      })
    );
  }

  setAltaRazonSocial(razonSocial: RazonSocial) {
    return this.http.post(this.config.getUrl('pymes.newContract.altaRazonSocial'), razonSocial).pipe(
      tap((res: GenericResponse<string>) => {
        this.razonSocial.next(res);
      })
    );
  }

  setAltaInstalacion(instalacion: InstallationSavedData) {
    return this.http.post(this.config.getUrl('pymes.newContract.altaInstalacion'), instalacion).pipe(
      tap((res: GenericResponse<string>) => {
        this.installationCode.next(res);
      })
    );
  }
  setAltaContrato(contrato: PaymentSavedData) {
    return this.http.post(this.config.getUrl('pymes.newContract.altaContrato'), contrato).pipe(
      tap((res: GenericResponse<{
        creadoAnteriormente: boolean,
        codigoCRMContrato: string
      }>) => {
        this.contractCode.next(res);
      })
    );
  }

  setFicheroContrato(params) {
    return this.http.post<GenericResponse>(this.config.getUrl('contrato.setFicheroContrato'), {}, { params }).pipe(
      tap((res: GenericResponse) => {
        return res;
      })
    );
  }

  getComercialesGestor(params: any) {
    return this.http.get(this.config.getUrl('pymes.newContract.searchCommercials'), { params }).pipe(
      tap((res: GenericResponse<IContactCRMSearchData>) => {
        this.contactoCRM.next(res);
      })
    );
  }

  setDraft(idBorrador: number, params: any) {
    return this.http.put(this.config.getUrl('pymes.newContract.draft', { idBorrador }), params);
  }

  getDraft(idBorrador: number) {
    this.setSavedData({ savedDataFromDraft: true, idBorrador });
    return this.http.get<GenericResponse>(this.config.getUrl('contrato.getDraftDetail', { idBorrador })).pipe(
      tap((res: GenericResponse<IDraftData>) => {
        return res;
      })
    );
  }

  firmaDigital(codContrato) {
    return this.http.post<GenericResponse>(this.config.getUrl('contrato.digitalSignature'), {}, { params: { codContrato } }).pipe(
      tap((res: GenericResponse) => {
        return res;
      })
    );
  }

  getContractTemplate(): Observable<any> {
    return this.http.get(this.config.getUrl('contrato.downloadTemplate'), {
      headers: { Accept: 'application/pdf' },
      responseType: 'blob'
    });
  }

  setAttachedFile(formData) {
    return this.http.post<GenericResponse>(this.config.getUrl('contrato.postAttachedFile'), formData).pipe(
      tap((res: GenericResponse) => {
        return res;
      })
    );
  }

  public setAttachedFileAsync(formData: FormData): Observable<GenericResponse> {
    return this.http.post<GenericResponse>(this.config.getUrl('contrato.postAttachedFileAsync'), formData);
  }

  public getPdfContract(codContrato: string, service: string): Observable<any> {
    return this.http.get(this.config.getUrl(service, { codContrato }), {
      headers: { Accept: 'application/pdf' },
      responseType: 'blob'
    });
  }

  public getContractFile(codContrato: string): Observable<HttpResponse<Blob>> {
    return this.http.get(this.config.getUrl('contrato.getFicheroContrato', { codContrato }), {
      headers: { Accept: 'application/pdf' },
      responseType: 'blob',
      observe: 'response'
    });
  }

  public getSepaMultipoint(codContrato: string): Observable<any> {
    return this.http.get(this.config.getUrl('contrato.getSepaMultipoint', { codContrato }), {
      headers: { Accept: 'application/x-zip-compressed' },
      responseType: 'blob',
      observe: 'response'
    });
  }

  public postCheckIbanRS(params: CheckIbanSRRequest): Observable<GenericResponse<CheckIbanSRResponse>> {
    return this.http.post<GenericResponse<CheckIbanSRResponse>>(this.config.getUrl('maestros.postCheckIbanRS'), params);
  }

  public getAttachmentsDraft(codContrato: string): Observable<GenericResponse<AttachmentsDraft>> {
    return this.http.get<GenericResponse<AttachmentsDraft>>(this.config.getUrl('contrato.getAttachmentsDraft', { codContrato }));
  }

  public postAttachmentsDraft(params: FormData): Observable<GenericResponse> {
    return this.http.post<GenericResponse>(this.config.getUrl('contrato.postAttachmentsDraft'), params);
  }

  public deleteAttachmentsDraft(idFicheroAdjunto: number): Observable<GenericResponse> {
    return this.http.delete<GenericResponse>(this.config.getUrl('contrato.deleteAttachmentsDraft', { idFicheroAdjunto }));
  }

  public getTensionCyclePeriod(): Observable<GenericResponse<Tensions>> {
    return this.http.get<GenericResponse<Tensions>>(this.config.getUrl('maestros.tensionCyclePeriod'));
  }

  public createContractAsync(contrato: PaymentSavedData): Observable<GenericResponse<CreateContractAsyncResponse>> {
    return this.http.post<GenericResponse<CreateContractAsyncResponse>>(this.config.getUrl('pymes.newContract.createContractAsync'), contrato);
  }

  public checkContractAsync(identifier: string): Observable<GenericResponse<CheckContractAsyncResponse>> {
    return this.http.get<GenericResponse<CheckContractAsyncResponse>>(this.config.getUrl('pymes.newContract.checkCreatedContractAsync', { identificador: identifier }));
  }

  public firmaDigitalAsync(codContrato: string): Observable<GenericResponse> {
    return this.http.post<GenericResponse>(this.config.getUrl('contrato.digitalSignatureAsync'), {}, { params: { codContrato } });
  }

  public setFicheroContratoAsync(codContrato: string) {
    return this.http.post<GenericResponse>(this.config.getUrl('contrato.setFicheroContratoAsync'), {}, { params: { codContrato } });
  }

}
